import React, { Dispatch, FC, SetStateAction, useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import Avatar from 'components/UI/Avatar';
import UILink from 'components/UI/Link';
import VerificationBadge from 'components/Profile/ProfileTop/VerificationBadge';

import { UnionExtraData, UserObjectType } from 'constants/graphqlTypes';
import { transformRoute } from 'helpers/routingHelper';
import usePageType from 'helpers/usePageType';
import getAuthUser from 'helpers/getAuthUser';
import { IContentIds } from 'helpers/useContentReactions';
import { TEST_ACTION_USERNAME } from 'constants/aqa/pickDetails';
import { ROUTE_USER_PROFILE } from 'routes';

import { generateFullName } from 'components/Profile/ProfileTop/helpers';
import { useReactionContext } from 'components/Reactions/hooks';
import { StyledUserInfo } from 'components/UI/Highlight/styled';
import { StyledUsername } from 'components/Sparks/Views/Card/styled';
import ReactionQuickButton from 'components/Reactions/ReactionQuickButton';
import ReactionsToken from 'components/Reactions/ReactionsToken';
import { ReactComponent as AddReaction } from 'assets/icons/AddReaction.svg';
import ReactionMenu from 'components/Reactions/ReactionMenu/ReactionMenu';
import { useHalfTransparency } from './hooks';

import {
  APPEAR_ANIMATION,
  StyledActionContent,
  StyledAvatarWithReactionContainer,
  StyledComment,
  StyledCommentInfo,
  StyledFollowingStatus,
  StyledName,
  StyledReactionContainer,
  StyledUserBio,
  StyledAvatarAnimationWrapper,
} from './styled';

interface ICommentProps {
  user: UserObjectType;
  fontColor: string;
  isDetail?: boolean;
  isSmall?: boolean;
  isSpotlight?: boolean;
  isSpotlightInFocus?: boolean;
  hasAnimation: boolean;
  withoutPadding?: boolean;
  isVirtualAction?: boolean;
  action?: UnionExtraData;
  id?: string;
  isReactionButtonMenuShown?: boolean;
  isReactionTokenModalOpen?: boolean;
  setIsReactionButtonMenuShown?: Dispatch<SetStateAction<boolean>>;
  setIsReactionsModalOpen?: Dispatch<SetStateAction<boolean>>;
  setIsReactionTokenModalOpen?: Dispatch<SetStateAction<boolean>>;
  setReactionCoordinates?: (coords: { x: number; y: number }) => void;
  setIsQuickReactionOpen?: Dispatch<SetStateAction<boolean>>;
  showAddReaction?: boolean;
  onOpenReactionsModal?: () => void;
  isProfilePicks?: boolean;
  isUserPicksImmersive?: boolean;
  hideAvatar?: boolean;
  hideUserInfo?: boolean;
  isListCard?: boolean;
  isCarousel?: boolean;
}

const Action: FC<ICommentProps> = ({
  children,
  user,
  fontColor,
  isDetail,
  isSmall,
  isSpotlight,
  isSpotlightInFocus,
  hasAnimation,
  withoutPadding,
  action,
  isReactionButtonMenuShown,
  setIsReactionButtonMenuShown,
  isVirtualAction,
  setIsReactionsModalOpen,
  isReactionTokenModalOpen,
  setIsReactionTokenModalOpen,
  id,
  setReactionCoordinates,
  setIsQuickReactionOpen,
  showAddReaction,
  onOpenReactionsModal,
  isProfilePicks,
  isUserPicksImmersive,
  hideAvatar = false,
  hideUserInfo = false,
  isListCard = false,
  isCarousel = false,
}) => {
  const { pickId } = useParams<{ pickId: string }>();
  const location = useLocation();
  const params = useParams<{ feedId?: string; pickId?: string }>();

  // Check if we're on the FeedItem route
  const isFeedItemRoute = Boolean(
    params.feedId && params.pickId && location.pathname.includes(`/feed/${params.feedId}/${params.pickId}`)
  );

  const { isItemDetail, isMyProfile } = usePageType();
  const { username: authenticateUsername } = getAuthUser();
  const { userInfo, username, isFollowedByMe, isVerified } = user;
  const { bio } = userInfo ?? {};
  const isAuthenticateUser = username === authenticateUsername;
  const avatarSize = isSmall ? 20 : 44;
  const isCurrentCardInFocus = isSpotlight || isDetail ? isSpotlightInFocus || pickId === id : true;

  const { isQuickReactionOpen, setPickerId } = useReactionContext();

  const { ref } = useHalfTransparency();
  const { ref: actionRef } = useHalfTransparency();

  // AddReaction menu state and ref
  const [reactionMenuCoords, setReactionMenuCoords] = useState<{ x: number; y: number } | undefined>(undefined);
  const addReactionButtonRef = useRef<HTMLButtonElement>(null);

  const openReactionMenu = () => {
    if (addReactionButtonRef.current) {
      const rect = addReactionButtonRef.current.getBoundingClientRect();
      const coords = {
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
      };
      setReactionMenuCoords(coords);
    }
    setIsReactionButtonMenuShown?.(true);
  };
  const closeReactionMenu = () => setIsReactionButtonMenuShown?.(false);

  const avatarRef = useRef<HTMLDivElement>(null);
  const [shouldAnimateAvatar, setShouldAnimateAvatar] = useState(false);
  const thoughtTextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPickerId?.(user.id);
  }, [user, setPickerId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldAnimateAvatar(true);
        }
      },
      {
        rootMargin: '-112px 0px 0px 0px',
        threshold: 0.1,
      }
    );
    if (avatarRef.current) observer.observe(avatarRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
        }
      },
      {
        rootMargin: '-112px 0px 0px 0px',
        threshold: 0.1,
      }
    );
    if (thoughtTextRef.current) observer.observe(thoughtTextRef.current);
    return () => observer.disconnect();
  }, []);

  const { id: actionId } = action ?? {};

  const contentIds: IContentIds =
    action?.__typename === 'CardCommentSchema'
      ? { commentId: action?.id, pickId: id || undefined }
      : { pickId: id || undefined };

  const [shouldRunAnimation, setShouldRunAnimation] = useState(false);

  useEffect(() => {
    // If we're not on a routed detail page (no pickId in params), show immediately
    if (!pickId) {
      setShouldRunAnimation(true);
      return undefined;
    }

    let timeout: number | undefined;
    if (pickId === id) {
      timeout = window.setTimeout(() => {
        setShouldRunAnimation(true);
      }, 5000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [pickId, id]);

  return (
    <StyledComment
      className="action-comment-wrap"
      fontColor={fontColor}
      isDetail={isDetail}
      isSpotlight={isSpotlight}
      isQuickReactionOpen={!!isQuickReactionOpen}
      isReactionButtonMenuShown={isReactionButtonMenuShown}
      isReactionTokenModalOpen={isReactionTokenModalOpen}
      hasAnimation={hasAnimation}
      withoutPadding={withoutPadding}
      isCardInFocus={isCurrentCardInFocus}
      animationDuration={isSpotlight ? '10s' : undefined}
      isProfilePicks={isProfilePicks}
      isUserPicksImmersive={isUserPicksImmersive}
      isSmall={isSmall}
    >
      <div>
        {!hideAvatar && (
          <StyledAvatarWithReactionContainer
            isCarousel={isCarousel}
            isSmall={isSmall}
            isListCard={isListCard}
            isSpotlight={isSpotlight}
          >
            <StyledAvatarAnimationWrapper
              ref={avatarRef}
              animate={shouldAnimateAvatar}
              isFeedItemRoute={isFeedItemRoute}
            >
              <Avatar user={user} dimension={avatarSize} clickable />
            </StyledAvatarAnimationWrapper>
            {isDetail && (
              <StyledReactionContainer isVirtualAction={isVirtualAction} hasLoadedReactions={shouldRunAnimation}>
                {!isAuthenticateUser && setIsReactionButtonMenuShown && setIsReactionsModalOpen && (
                  <>
                    <ReactionQuickButton
                      isReactionButtonMenuShown={!!isQuickReactionOpen}
                      setIsReactionButtonMenuShown={setIsQuickReactionOpen}
                      isDark
                      entityId={actionId ?? ''}
                      isReactionMenuOpened={isQuickReactionOpen}
                      setReactionCoordinates={setReactionCoordinates}
                    />
                  </>
                )}
                <ReactionsToken
                  isDetail={isDetail}
                  className={APPEAR_ANIMATION}
                  isVertical
                  hideSecondEmoji
                  contentIds={contentIds}
                  isDark
                  setIsModalOpen={setIsReactionTokenModalOpen}
                />
              </StyledReactionContainer>
            )}
          </StyledAvatarWithReactionContainer>
        )}
        <StyledCommentInfo isSmall={isSmall}>
          <div ref={ref}>
            {!hideUserInfo && (
              <>
                <StyledUserInfo className="action-user-info">
                  <UILink route={transformRoute(ROUTE_USER_PROFILE, { profileName: username })}>
                    <StyledName>
                      <StyledUsername data-testid={TEST_ACTION_USERNAME}>{generateFullName(user)}</StyledUsername>
                      {isVerified && <VerificationBadge />}
                      {!isMyProfile && isFollowedByMe && (
                        <StyledFollowingStatus>&nbsp;·&nbsp;Following</StyledFollowingStatus>
                      )}
                    </StyledName>
                  </UILink>
                </StyledUserInfo>
                {bio && (
                  <StyledUserBio className="action-user-info" isSmall={!isItemDetail}>
                    {bio}
                  </StyledUserBio>
                )}
              </>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: isProfilePicks ? '0' : '10px',
            }}
          >
            <StyledActionContent isDetail={isDetail} ref={actionRef} style={{ color: fontColor }}>
              {children}
            </StyledActionContent>
            {showAddReaction && (
              <div
                style={{
                  position: 'relative',
                  zIndex: 100,
                  pointerEvents: 'auto',
                  marginLeft: 'auto',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <button
                  ref={addReactionButtonRef}
                  type="button"
                  style={{
                    height: '24px',
                    width: '24px',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    position: 'relative',
                    zIndex: 100,
                    pointerEvents: 'auto',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openReactionMenu();
                  }}
                  aria-label="Add Reaction"
                >
                  <AddReaction style={{ color: fontColor }} />
                </button>
                {isReactionButtonMenuShown && (
                  <ReactionMenu
                    isOpen={isReactionButtonMenuShown}
                    anchorPoint={reactionMenuCoords ?? { x: 0, y: 0 }}
                    onClose={closeReactionMenu}
                    onSend={() => {}}
                    onEmoji={() => {}}
                    onOpenReactionsModal={onOpenReactionsModal}
                  />
                )}
              </div>
            )}
          </div>
        </StyledCommentInfo>
      </div>
    </StyledComment>
  );
};

export default Action;
